/**
 * Create Recurring Schedule Modal
 * Allows company administrators to create new recurring schedules
 */

import { useState, useRef, useEffect, type FormEvent } from 'react';
import { createRecurringSchedule } from '../../../services/recurringSchedule.service';
import type { RecurrenceFrequency, UserProfile } from '../../../../../shared/types';
import { getCompanyUsers } from '../../../services/user.service';
import { ErrorMessage } from '../../ErrorMessage';
import { BaseModal } from '../../common/BaseModal';
import { FormInputField } from '../../form/FormInputField';
import { FormTextareaField } from '../../form/FormTextareaField';
import { FormSelectField } from '../../form/FormSelectField';
import { LocationAutocompleteField } from '../../form/LocationAutocompleteField';
import { ModalButtonGroup } from '../../common/ModalButtonGroup';
import { FORM_GROUP_STYLE, LABEL_STYLE, INPUT_STYLE } from '../../../styles/modalStyles';
import { SELECT_STYLE } from '../../../styles/components';
import { DAYS_OF_WEEK, FREQUENCY_OPTIONS, DEFAULT_WORKING_DAYS, DEFAULT_SCHEDULE_TIMES } from '../../../constants/schedule';

const getTodayDateString = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

interface CreateRecurringScheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyId: string;
  onSuccess: () => void;
}

const EMPTY_FORM = (today: string): FormData => ({
  title: '',
  description: '',
  address: '',
  latitude: 0,
  longitude: 0,
  hourlyRate: 0,
  recurrenceFrequency: 'weekly',
  daysOfWeek: [...DEFAULT_WORKING_DAYS],
  startTime: DEFAULT_SCHEDULE_TIMES.startTime,
  endTime: DEFAULT_SCHEDULE_TIMES.endTime,
  effectiveFrom: today,
  effectiveUntil: '',
});

interface FormData {
  title: string;
  description: string;
  address: string;
  latitude: number;
  longitude: number;
  hourlyRate: number;
  recurrenceFrequency: RecurrenceFrequency;
  daysOfWeek: number[];
  startTime: string;
  endTime: string;
  effectiveFrom: string;
  effectiveUntil: string;
}

export function CreateRecurringScheduleModal({
  isOpen,
  onClose,
  companyId,
  onSuccess,
}: CreateRecurringScheduleModalProps) {
  const [formData, setFormData] = useState<FormData>(EMPTY_FORM(getTodayDateString()));
  const [selectedManagerId, setSelectedManagerId] = useState('');
  const [selectedStaffId, setSelectedStaffId] = useState('');
  const [managers, setManagers] = useState<UserProfile[]>([]);
  const [staff, setStaff] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const formDataRef = useRef(formData);

  useEffect(() => {
    if (!isOpen) return;
    getCompanyUsers(companyId).then((users) => {
      setManagers(users.filter((u: UserProfile) => u.userType === 'manager' && !u.isArchived));
      setStaff(users.filter((u: UserProfile) => u.userType === 'staff' && !u.isArchived));
    });
  }, [isOpen, companyId]);

  formDataRef.current = formData;

  const handleDayToggle = (day: number) => {
    setFormData((prev) => ({
      ...prev,
      daysOfWeek: prev.daysOfWeek.includes(day)
        ? prev.daysOfWeek.filter((d) => d !== day)
        : [...prev.daysOfWeek, day].sort((a, b) => a - b),
    }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    const data = formDataRef.current;

    // Validation
    if (!data.title.trim()) {
      setError('Schedule title is required');
      return;
    }

    if (!data.address.trim()) {
      setError('Location address is required');
      return;
    }

    if (data.latitude === 0 && data.longitude === 0) {
      setError('Please select an address from the suggestions to set GPS coordinates');
      return;
    }

    if (data.hourlyRate <= 0) {
      setError('Hourly rate must be greater than 0');
      return;
    }

    if (data.daysOfWeek.length === 0) {
      setError('At least one day must be selected');
      return;
    }

    if (data.startTime === data.endTime) {
      setError('Start time and end time cannot be the same');
      return;
    }

    setLoading(true);

    try {
      await createRecurringSchedule({
        companyId,
        title: data.title.trim(),
        description: data.description.trim() || undefined,
        location: {
          address: data.address.trim(),
          latitude: data.latitude,
          longitude: data.longitude,
        },
        hourlyRate: data.hourlyRate,
        recurrenceFrequency: data.recurrenceFrequency,
        daysOfWeek: data.daysOfWeek,
        startTime: data.startTime,
        endTime: data.endTime,
        effectiveFrom: new Date(data.effectiveFrom),
        effectiveUntil: data.effectiveUntil ? new Date(data.effectiveUntil) : undefined,
        assignedManagerId: selectedManagerId || undefined,
        userIds: selectedStaffId ? [selectedStaffId] : undefined,
      });

      onSuccess();
      onClose();

      // Reset form
      setFormData(EMPTY_FORM(getTodayDateString()));
      setSelectedManagerId('');
      setSelectedStaffId('');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to create recurring schedule');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="Create Recurring Schedule"
      size="medium"
      loading={loading}
    >
      <form onSubmit={handleSubmit}>
        <FormInputField
          label="Schedule Title"
          value={formData.title}
          onChange={(value) => setFormData({ ...formData, title: value })}
          placeholder="e.g., Morning Shift Security"
          required
        />

        <FormTextareaField
          label="Description"
          value={formData.description}
          onChange={(value) => setFormData({ ...formData, description: value })}
          placeholder="Job details and requirements..."
          rows={2}
        />

        <LocationAutocompleteField
          label="Location Address"
          onChange={(result) => setFormData((prev) => ({
            ...prev,
            address: result.address,
            latitude: result.latitude,
            longitude: result.longitude,
          }))}
          required
        />

        <FormInputField
          label="Hourly Rate (€)"
          type="number"
          value={formData.hourlyRate.toString()}
          onChange={(value) => setFormData({ ...formData, hourlyRate: parseFloat(value) || 0 })}
          min="0"
          step="0.01"
          placeholder="15.00"
          required
        />

        <FormSelectField
          label="Frequency"
          value={formData.recurrenceFrequency}
          onChange={(value) => setFormData({ ...formData, recurrenceFrequency: value as RecurrenceFrequency })}
          options={FREQUENCY_OPTIONS}
        />

        {/* Days of Week Selection */}
        <div style={FORM_GROUP_STYLE}>
          <label style={LABEL_STYLE}>
            Days of Week <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <div style={{ display: 'flex', gap: '6px' }}>
            {DAYS_OF_WEEK.map((day) => {
              const selected = formData.daysOfWeek.includes(day.value);
              return (
                <button
                  key={day.value}
                  type="button"
                  onClick={() => handleDayToggle(day.value)}
                  style={{
                    flex: 1,
                    padding: '7px 0',
                    border: '1.5px solid',
                    borderColor: selected ? '#3b82f6' : 'transparent',
                    borderRadius: '20px',
                    backgroundColor: selected ? '#eff6ff' : '#f3f4f6',
                    color: selected ? '#1d4ed8' : '#9ca3af',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: selected ? 700 : 500,
                    transition: 'all 0.15s ease',
                    outline: 'none',
                  }}
                >
                  {day.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Time Selection - Two columns */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <div style={FORM_GROUP_STYLE}>
            <label style={LABEL_STYLE}>
              Start Time <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input
              type="time"
              value={formData.startTime}
              onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
              style={INPUT_STYLE}
              required
            />
          </div>
          <div style={FORM_GROUP_STYLE}>
            <label style={LABEL_STYLE}>
              End Time <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input
              type="time"
              value={formData.endTime}
              onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
              style={INPUT_STYLE}
              required
            />
            {formData.startTime && formData.endTime && formData.endTime < formData.startTime && (
              <span style={{ display: 'block', marginTop: '4px', fontSize: '12px', color: '#6b7280' }}>
                Overnight shift — ends the following day
              </span>
            )}
          </div>
        </div>

        {/* Effective Date Range - Two columns */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <FormInputField
            label="Effective From"
            type="date"
            value={formData.effectiveFrom}
            onChange={(value) => setFormData({ ...formData, effectiveFrom: value })}
            required
          />
          <FormInputField
            label="Effective Until"
            type="date"
            value={formData.effectiveUntil}
            onChange={(value) => setFormData({ ...formData, effectiveUntil: value })}
            helperText="Leave empty for indefinite"
          />
        </div>

        {/* Optional Assignment */}
        {(managers.length > 0 || staff.length > 0) && (
          <div style={FORM_GROUP_STYLE}>
            <label style={LABEL_STYLE}>Assign To (Optional)</label>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
              {managers.length > 0 && (
                <select
                  value={selectedManagerId}
                  onChange={(e) => {
                    setSelectedManagerId(e.target.value);
                    if (e.target.value) setSelectedStaffId('');
                  }}
                  disabled={!!selectedStaffId}
                  style={{
                    ...SELECT_STYLE,
                    opacity: selectedStaffId ? 0.5 : 1,
                    cursor: selectedStaffId ? 'not-allowed' : 'pointer',
                  }}
                  title={selectedStaffId ? 'Clear staff selection first' : undefined}
                >
                  <option value="">Assign to Manager...</option>
                  {managers.map((m) => (
                    <option key={m.uid} value={m.uid}>
                      {m.firstName} {m.lastName}
                    </option>
                  ))}
                </select>
              )}
              {managers.length > 0 && staff.length > 0 && (
                <span style={{ fontSize: '12px', color: '#9ca3af' }}>or</span>
              )}
              {staff.length > 0 && (
                <select
                  value={selectedStaffId}
                  onChange={(e) => {
                    setSelectedStaffId(e.target.value);
                    if (e.target.value) setSelectedManagerId('');
                  }}
                  disabled={!!selectedManagerId}
                  style={{
                    ...SELECT_STYLE,
                    opacity: selectedManagerId ? 0.5 : 1,
                    cursor: selectedManagerId ? 'not-allowed' : 'pointer',
                  }}
                  title={selectedManagerId ? 'Clear manager selection first' : undefined}
                >
                  <option value="">Assign to Staff (Direct)...</option>
                  {staff.map((s) => (
                    <option key={s.uid} value={s.uid}>
                      {s.firstName} {s.lastName}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>
        )}

        {error && <ErrorMessage message={error} />}

        <ModalButtonGroup
          onCancel={onClose}
          submitLabel="Create Schedule"
          isLoading={loading}
          loadingLabel="Creating..."
        />
      </form>
    </BaseModal>
  );
}
